<template>
  <div class="input-wrapper">
    <input
      v-model="inputValueTitle"
      type="text"
      :class="['custom-input', customInputClass]"
      @input="(e) => onInput(e.target.value)"
      @focus="isFocus = true"
      @blur="onBlur"
    />
    <div class="arrow-container">
      <div
        class="tw-flex tw-items-end tw-flex-1 tw-cursor-pointer"
        @click="increment"
      >
        <div class="arrow arrow-up"></div>
      </div>
      <div class="tw-flex tw-flex-1 tw-cursor-pointer" @click="decrement">
        <div class="arrow arrow-down"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { getComponentUiText } from "@/utils/utilities";
import { ref, computed, defineProps, defineEmits, watch } from "vue";

const props = defineProps({
  max: {
    type: Number,
    default: Infinity,
  },
  paging: {
    type: Number,
    default: Infinity,
  },
  type: {
    type: String,
    default: "quickwin",
  },
  customInputClass: {
    type: String,
    default: "",
  },
});
const isFocus = ref(false);

const emit = defineEmits(["arrow-click"]);

const inputValueNumber = computed(() => props.paging);

const inputValueTitle = computed(() => {
  return isFocus.value
    ? inputValueNumber.value
    : Number(inputValueNumber.value) === Number(props.max) ||
        !inputValueNumber.value
      ? `${getComponentUiText(
          "app-component-sharing-quick-wins-display-all-1",
        )}`
      : `${getComponentUiText(
          "app-component-sharing-quick-wins-display-top-1",
        )} ${inputValueNumber.value}`;
});

watch(
  () => props.max,
  (newValue) => {
    inputValueNumber.value = props.paging;
  },
  { immediate: true },
);

const current = computed(() => {
  const n = parseInt(inputValueNumber.value);
  return isNaN(n) ? 0 : n;
});

const isMin = computed(() => current.value <= 1);
const isMax = computed(() => current.value >= props.max);

const increment = () => {
  if (isMax.value) return;
  const newValue = current.value + 1;
  inputValueNumber.value = String(newValue);
  emit("arrow-click", { value: newValue });
};

const decrement = () => {
  if (isMin.value) return;
  const newValue = current.value - 1;
  inputValueNumber.value = String(newValue);
  emit("arrow-click", { value: newValue });
};

const onInput = (e) => {
  e = e.replace(/\D/g, "");
  inputValueNumber.value = e;
};

const onBlur = () => {
  isFocus.value = false;
  let n = parseInt(inputValueNumber.value);
  if (n > props.max || n < 1 || isNaN(n)) n = props.max;
  inputValueNumber.value = String(n);
  emit("arrow-click", { value: n });
};
</script>

<style scoped>
.input-wrapper {
  position: relative;
  display: inline-block;
}

.input-wrapper input {
  padding-right: 30px;
  height: 44px;
  width: 160px;
  box-sizing: border-box;
}

.arrow-container {
  position: absolute;
  right: 5px;
  top: 50%;
  height: 44px;
  align-items: center;
  justify-content: center;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  opacity: 0;
  transition: opacity 0.2s;
}

.input-wrapper:hover .arrow-container {
  opacity: 1;
}

.arrow {
  width: 0;
  height: 0;
  margin: 2px 0;
}

.arrow-up {
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 6px solid #555;
}

.arrow-down {
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #555;
}

.disabled {
  opacity: 0.3;
  pointer-events: none;
}

.custom-input {
  border-radius: 6px;
  border: 1px solid var(--UI-rule, #eff0f1);
  background: rgba(239, 240, 241, 0.5);
  padding: 6px 10px;
  outline: none;
  transition: border-color 0.2s;
}

.custom-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.2);
}
</style>
